import { parseServerDate } from '@/lib/format';

/** Soft user-facing AI failure copy — raw provider text stays in DB only. */
export const GENERIC_EVAL_FAILURE_MSG =
  'Something went wrong while generating this evaluation. Please try again later, or contact the support team if it continues.';

/** Client magazine section when an included artefact is not client-grade. */
export const CLIENT_ARTEFACT_UNAVAILABLE_MSG =
  'Something went wrong with this section. Please contact the Kadel Labs support team.';

/** Client-side safety net if backend reclaim is delayed (ms). Match backend reclaim. */
export const EVAL_UI_STALE_MS = 15 * 60 * 1000;

/**
 * True when an `in_progress` eval has outlived the backend reclaim window.
 *
 * `eval_started_at` arrives as naive UTC with no timezone designator, so it must
 * go through `parseServerDate`. Bare `Date.parse` reads it as browser-local time,
 * which in IST makes every fresh run look 5h30m old and instantly "stale".
 */
export function isEvalUiStale(evalStartedAt) {
  const d = parseServerDate(evalStartedAt);
  if (!d) return false;
  return Date.now() - d.getTime() >= EVAL_UI_STALE_MS;
}

/** Patch candidate GET cache so polling and UI leave sticky `failed` immediately. */
export function applyEvalPendingToCandidateData(data, { stage, entityId }) {
  if (!data || !entityId) return data;

  if (stage === 'videobot') {
    const responses = data.videobot?.responses;
    if (!Array.isArray(responses)) return data;
    let touched = false;
    const nextResponses = responses.map((r) => {
      if (r.id !== entityId) return r;
      touched = true;
      return {
        ...r,
        eval_status: 'pending',
        eval_started_at: null,
        eval_completed_at: null,
      };
    });
    if (!touched) return data;
    return {
      ...data,
      videobot: { ...data.videobot, responses: nextResponses },
    };
  }

  if (stage === 'mcq') {
    if (!data.mcq || data.mcq.id !== entityId) return data;
    return {
      ...data,
      mcq: {
        ...data.mcq,
        eval: {
          ...(data.mcq.eval || {}),
          eval_status: 'pending',
          eval_started_at: null,
          eval_completed_at: null,
        },
      },
    };
  }

  if (stage === 'tech') {
    if (!data.tech_assessment || data.tech_assessment.id !== entityId) return data;
    return {
      ...data,
      tech_assessment: {
        ...data.tech_assessment,
        eval: {
          ...(data.tech_assessment.eval || {}),
          eval_status: 'pending',
          eval_started_at: null,
          eval_completed_at: null,
        },
      },
    };
  }

  return data;
}

/** Optimistic React Query patch for all `v1-candidate` queries for this id. */
export function patchCandidateEvalPending(qc, candidateId, { stage, entityId }) {
  if (!qc || !candidateId || !entityId) return;
  qc.setQueriesData({ queryKey: ['v1-candidate', candidateId] }, (old) => (
    applyEvalPendingToCandidateData(old, { stage, entityId })
  ));
}
